import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/api';

// Boost tier pricing - matches backend pricing model
const BOOST_TIERS = [
  { min: 0, max: 25, name: 'Light', pricePerK: 0.50, multiplier: 1.0, description: 'Slight visibility increase', color: 'slate' },
  { min: 26, max: 50, name: 'Standard', pricePerK: 1.00, multiplier: 1.5, description: 'Balanced exposure boost', color: 'blue' },
  { min: 51, max: 75, name: 'Premium', pricePerK: 2.50, multiplier: 2.5, description: 'Priority in feeds', color: 'purple' },
  { min: 76, max: 100, name: 'Viral', pricePerK: 5.00, multiplier: 5.0, description: 'Maximum algorithm weight', color: 'amber' },
];

// Budget limits matching backend
const MIN_BUDGET = 5;
const MAX_BUDGET = 100000;
const POPULAR_BUDGETS = [25, 50, 100, 250, 500, 1000, 2500, 5000];
const BASE_RATE_PER_1K = 1.00;

const getBoostTier = (weight) => BOOST_TIERS.find(t => weight >= t.min && weight <= t.max) || BOOST_TIERS[0];

// Calculate cost per 1K impressions based on weight
const calculateCostPer1K = (weight) => {
  const tier = getBoostTier(weight);
  const weightFactor = Math.max(weight / 50, 0.5);
  return Math.round(BASE_RATE_PER_1K * weightFactor * tier.multiplier * 100) / 100;
};

// Calculate estimated impressions from budget
const calculateEstimatedImpressions = (weight, budget) => {
  const costPer1K = calculateCostPer1K(weight);
  if (costPer1K <= 0) return 0;
  return Math.floor((budget / costPer1K) * 1000);
};

const formatCurrency = (amount) => `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatNumber = (num) => Number(num).toLocaleString('en-US');

export default function VideoBoost() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [boostWeight, setBoostWeight] = useState(50);
  const [budget, setBudget] = useState(100); // User-defined budget
  const [customBudget, setCustomBudget] = useState(''); // For manual input
  const [duration, setDuration] = useState(7);
  const [targetPool, setTargetPool] = useState('for-you');
  const [selectedVideo, setSelectedVideo] = useState(location.state?.videoId || null);
  const [userVideos, setUserVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [boostHistory, setBoostHistory] = useState([]);

  const currentTier = getBoostTier(boostWeight);
  const costPer1K = useMemo(() => calculateCostPer1K(boostWeight), [boostWeight]);
  const estimatedImpressions = useMemo(() => calculateEstimatedImpressions(boostWeight, budget), [boostWeight, budget]);
  const dailyBudget = budget / duration;
  const dailyImpressions = Math.floor(estimatedImpressions / duration);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch user's videos
        const videosRes = await apiService.getUserVideos?.(user?.id) || { videos: [] };
        setUserVideos(videosRes.videos || []);

        // Fetch wallet balance
        const walletRes = await apiService.getWallet?.() || { balance: 0 };
        setWalletBalance(walletRes.balance || 0);

        // Fetch boost history
        const historyRes = await apiService.request?.('/api/boost/history') || { boosts: [] };
        setBoostHistory(historyRes.boosts || []);
      } catch (err) {
        console.error('Failed to load boost data:', err);
      }
    };

    fetchData();
  }, [isAuthenticated, user?.id, navigate]);

  const handleCreateBoost = async () => {
    if (!selectedVideo) {
      alert('Please select a video to boost');
      return;
    }

    if (budget > walletBalance) {
      alert('Insufficient balance. Please add funds to your wallet.');
      return;
    }

    if (budget < MIN_BUDGET) {
      alert(`Minimum budget is ${formatCurrency(MIN_BUDGET)}`);
      return;
    }

    setLoading(true);
    try {
      await apiService.request?.('/api/boost/create', {
        method: 'POST',
        body: JSON.stringify({
          video_id: selectedVideo,
          weight: boostWeight,
          budget: budget,
          duration_days: duration,
          target_pool: targetPool
        })
      });

      alert('Boost campaign created successfully!');
      navigate('/profile');
    } catch (err) {
      console.error('Failed to create boost:', err);
      alert('Failed to create boost campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBudgetChange = (value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue >= MIN_BUDGET && numValue <= MAX_BUDGET) {
      setBudget(numValue);
      setCustomBudget('');
    }
  };

  const handleCustomBudgetSubmit = () => {
    const numValue = parseFloat(customBudget) || 0;
    if (numValue >= MIN_BUDGET && numValue <= MAX_BUDGET) {
      setBudget(numValue);
      setCustomBudget('');
    } else if (numValue < MIN_BUDGET) {
      alert(`Minimum budget is ${formatCurrency(MIN_BUDGET)}`);
    } else if (numValue > MAX_BUDGET) {
      alert(`Maximum budget is ${formatCurrency(MAX_BUDGET)}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white pb-24">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 px-4 py-2 rounded-full border border-amber-500/30 mb-4">
            <span className="text-xl">🚀</span>
            <span className="text-amber-400 font-semibold">Creator Pro</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black mb-2">Video Boost</h1>
          <p className="text-white/60">Promote your video to reach more viewers</p>
        </div>

        {/* Video Selection */}
        <section className="bg-slate-900/50 border border-white/5 rounded-2xl p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <span>📹</span> Select Video to Boost
          </h2>
          
          {userVideos.length === 0 ? (
            <div className="text-center py-8 text-white/50">
              <p>No videos found. Upload a video first!</p>
              <button 
                onClick={() => navigate('/upload')}
                className="mt-4 px-6 py-2 bg-purple-500 hover:bg-purple-400 rounded-lg font-medium transition"
              >
                Upload Video
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {userVideos.slice(0, 6).map((video) => (
                <button
                  key={video.id}
                  onClick={() => setSelectedVideo(video.id)}
                  className={`relative aspect-video rounded-xl overflow-hidden border-2 transition-all ${
                    selectedVideo === video.id 
                      ? 'border-amber-500 ring-2 ring-amber-500/50' 
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  {video.thumbnail_url ? (
                    <img src={video.thumbnail_url} alt={video.caption} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                      <span className="text-2xl">🎬</span>
                    </div>
                  )}
                  {selectedVideo === video.id && (
                    <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                      <span className="text-2xl">✓</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Boost Weight Slider */}
        <section className="bg-slate-900/50 border border-white/5 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <span>⚡</span> Boost Power
            </h2>
            <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
              currentTier.name === 'Viral' ? 'bg-amber-500/20 text-amber-400' :
              currentTier.name === 'Premium' ? 'bg-purple-500/20 text-purple-400' :
              currentTier.name === 'Standard' ? 'bg-blue-500/20 text-blue-400' :
              'bg-slate-500/20 text-slate-400'
            }`}>
              {currentTier.name} Tier
            </div>
          </div>
          
          <p className="text-sm text-white/60 mb-6">{currentTier.description}</p>
          
          {/* Slider */}
          <div className="space-y-4">
            <div className="relative">
              <input
                type="range"
                min="0"
                max="100"
                value={boostWeight}
                onChange={(e) => setBoostWeight(Number(e.target.value))}
                className="w-full h-3 rounded-full appearance-none cursor-pointer bg-gradient-to-r from-slate-600 via-blue-500 via-purple-500 to-amber-500"
                style={{
                  background: `linear-gradient(to right, 
                    rgb(71 85 105) 0%, 
                    rgb(71 85 105) ${Math.min(boostWeight, 25)}%, 
                    rgb(59 130 246) ${Math.min(boostWeight, 25)}%, 
                    rgb(59 130 246) ${Math.min(boostWeight, 50)}%,
                    rgb(168 85 247) ${Math.min(boostWeight, 50)}%,
                    rgb(168 85 247) ${Math.min(boostWeight, 75)}%,
                    rgb(245 158 11) ${Math.min(boostWeight, 75)}%,
                    rgb(245 158 11) 100%
                  )`
                }}
              />
              <div className="flex justify-between text-xs text-white/40 mt-2">
                <span>Light</span>
                <span>Standard</span>
                <span>Premium</span>
                <span>Viral</span>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <div className="bg-black/40 rounded-2xl px-8 py-4 text-center">
                <div className="text-5xl font-black bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                  {boostWeight}
                </div>
                <div className="text-sm text-white/50 mt-1">Algorithm Weight</div>
              </div>
            </div>
            
            <p className="text-xs text-white/40 text-center">
              Higher weight = More prominent placement in feeds. Videos with higher boost weights appear earlier in user feeds.
            </p>
          </div>
        </section>

        {/* Target Pool */}
        <section className="bg-slate-900/50 border border-white/5 rounded-2xl p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <span>🎯</span> Target Audience
          </h2>
          
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'for-you', label: 'For You Feed', icon: '✨', desc: 'Personalized recommendations' },
              { id: 'trending', label: 'Trending', icon: '🔥', desc: 'Hot content section' },
              { id: 'discover', label: 'Discover', icon: '🔍', desc: 'Explore page placement' },
              { id: 'community', label: 'Communities', icon: '👥', desc: 'Relevant community feeds' },
            ].map((pool) => (
              <button
                key={pool.id}
                onClick={() => setTargetPool(pool.id)}
                className={`p-4 rounded-xl border text-left transition-all ${
                  targetPool === pool.id
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">{pool.icon}</span>
                  <span className="font-medium">{pool.label}</span>
                </div>
                <p className="text-xs text-white/50">{pool.desc}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Campaign Settings */}
        <section className="bg-slate-900/50 border border-white/5 rounded-2xl p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <span>💵</span> Campaign Budget
          </h2>
          
          <div className="grid gap-6">
            {/* Budget Selection */}
            <div>
              <label className="block text-sm text-white/60 mb-2">Select Budget (or enter custom amount)</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {POPULAR_BUDGETS.map((val) => (
                  <button
                    key={val}
                    onClick={() => handleBudgetChange(val)}
                    className={`py-2 px-4 rounded-lg text-sm font-medium transition ${
                      budget === val
                        ? 'bg-amber-500 text-black'
                        : 'bg-white/5 text-white/70 hover:bg-white/10'
                    }`}
                  >
                    ${val.toLocaleString()}
                  </button>
                ))}
              </div>
              
              {/* Custom Budget Input */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">$</span>
                  <input
                    type="number"
                    value={customBudget}
                    onChange={(e) => setCustomBudget(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCustomBudgetSubmit()}
                    placeholder="Enter custom amount..."
                    min={MIN_BUDGET}
                    max={MAX_BUDGET}
                    className="w-full pl-8 pr-4 py-3 bg-black/30 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-amber-500/50 focus:outline-none"
                  />
                </div>
                <button
                  onClick={handleCustomBudgetSubmit}
                  disabled={!customBudget}
                  className="px-6 py-3 bg-amber-500/20 text-amber-400 rounded-xl font-medium hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Set
                </button>
              </div>
              <p className="text-xs text-white/40 mt-2">Min: ${MIN_BUDGET} • Max: ${MAX_BUDGET.toLocaleString()}</p>
            </div>
            
            {/* Duration */}
            <div>
              <label className="block text-sm text-white/60 mb-2">Campaign Duration</label>
              <div className="flex flex-wrap gap-2">
                {[1, 3, 7, 14, 30, 60, 90].map((days) => (
                  <button
                    key={days}
                    onClick={() => setDuration(days)}
                    className={`py-2 px-4 rounded-lg text-sm font-medium transition ${
                      duration === days
                        ? 'bg-amber-500 text-black'
                        : 'bg-white/5 text-white/70 hover:bg-white/10'
                    }`}
                  >
                    {days === 1 ? '1 day' : `${days} days`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Campaign Summary */}
        <section className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <span>📊</span> Campaign Summary
          </h2>
          
          <div className="space-y-3">
            {/* Total Budget - Big and prominent */}
            <div className="bg-black/30 rounded-xl p-4 text-center mb-4">
              <div className="text-white/50 text-sm mb-1">Your Budget</div>
              <div className="text-4xl font-black text-amber-400">{formatCurrency(budget)}</div>
              <div className="text-white/40 text-xs mt-1">over {duration} days</div>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Boost Tier</span>
              <span className={`font-medium px-2 py-0.5 rounded-full text-xs ${
                currentTier.color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
                currentTier.color === 'purple' ? 'bg-purple-500/20 text-purple-400' :
                currentTier.color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                'bg-slate-500/20 text-slate-400'
              }`}>
                {currentTier.name}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Cost Per 1K Impressions</span>
              <span className="font-medium">{formatCurrency(costPer1K)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Estimated Total Impressions</span>
              <span className="font-medium text-emerald-400">{formatNumber(estimatedImpressions)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Daily Budget</span>
              <span className="font-medium">{formatCurrency(dailyBudget)}/day</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Daily Impressions (est.)</span>
              <span className="font-medium">{formatNumber(dailyImpressions)}/day</span>
            </div>
            
            {/* Explanation */}
            <div className="bg-black/20 rounded-lg p-3 text-xs text-white/50 mt-2">
              <p className="mb-1"><strong>How it works:</strong></p>
              <p>Higher weight = more prominent placement, but costs more per impression.</p>
              <p className="mt-1">Weight {boostWeight} at {currentTier.name} tier = {formatCurrency(costPer1K)}/1K impressions</p>
              <p className="mt-1">Budget {formatCurrency(budget)} ÷ {formatCurrency(costPer1K)}/1K = ~{formatNumber(estimatedImpressions)} impressions</p>
            </div>
            
            {/* Wallet Balance */}
            <div className="border-t border-amber-500/30 pt-3 mt-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-white/50">Your Wallet Balance</span>
                <span className={`font-medium ${walletBalance >= budget ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(walletBalance)}
                  {walletBalance < budget && (
                    <button 
                      onClick={() => navigate('/wallet')}
                      className="ml-2 text-xs underline"
                    >
                      Add funds
                    </button>
                  )}
                </span>
              </div>
              {walletBalance >= budget && (
                <div className="flex justify-between items-center text-sm mt-2">
                  <span className="text-white/50">After Campaign</span>
                  <span className="font-medium text-white/70">{formatCurrency(walletBalance - budget)}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 py-4 rounded-xl bg-white/5 hover:bg-white/10 font-semibold transition"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateBoost}
            disabled={loading || !selectedVideo || budget > walletBalance}
            className="flex-1 py-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 font-bold text-black disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg shadow-amber-500/30"
          >
            {loading ? 'Creating...' : budget > walletBalance ? 'Insufficient Balance' : `Launch Campaign • ${formatCurrency(budget)} 🚀`}
          </button>
        </div>

        {/* Boost History */}
        {boostHistory.length > 0 && (
          <section className="bg-slate-900/50 border border-white/5 rounded-2xl p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <span>📊</span> Previous Campaigns
            </h2>
            
            <div className="space-y-3">
              {boostHistory.map((boost, idx) => (
                <div key={boost.id || idx} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      🎬
                    </div>
                    <div>
                      <p className="font-medium text-sm">Video #{boost.video_id}</p>
                      <p className="text-xs text-white/50">Weight: {boost.weight} • {boost.impressions_delivered?.toLocaleString() || 0} impressions</p>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    boost.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                    boost.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-slate-500/20 text-slate-400'
                  }`}>
                    {boost.status}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
